// Gate 5.2 · Publish Composer
// Operator surface for creating a Publish Plan and then either
// publishing it now or scheduling it for later. Minimal but complete:
//
//   1. Pick an approved asset set (any campaign)
//   2. Pick a channel + channel_connection
//   3. Author channel_config (headline / body / page_message etc)
//   4. Run preflight
//   5. Choose "Publish now" or "Schedule for later"
//
// The full "campaign → asset set drill-down" experience lives elsewhere;
// this page exists so operators can drive the scheduler + Facebook
// smoke without leaving Brand OS.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

const CHANNEL_LABELS = {
  facebook:      "Facebook (Meta Pages)",
  instagram:     "Instagram (Meta Business)",
  email:         "Email (Resend)",
  youtube:       "YouTube",
  tiktok:        "TikTok",
  pinterest:     "Pinterest",
  manual_export: "Manual Export",
};
// Always-available channels (no credential store required to pick).
const ALWAYS_ON_CHANNELS = ["manual_export"];
// Which channels support each surface. The server's adapter registry
// (/publish/surface-schemas) is the real source of truth; this UI
// table decides which Surface radio buttons to render in New Publish.
// P5.4 (2026-11-03) · Item 2 — Expanded to every surface in the
// acceptance matrix: FB/IG feed/story/reel/carousel/video_story,
// YouTube short/long/custom_thumbnail, TikTok feed/photo, Pinterest
// pin/video_pin/carousel.
const CHANNEL_SURFACES = {
  facebook:  ["feed", "story", "reel", "carousel", "video_story"],
  instagram: ["feed", "story", "reel", "carousel", "video_story"],
  youtube:   ["short", "long", "custom_thumbnail"],
  tiktok:    ["feed", "photo"],
  pinterest: ["pin", "video_pin", "carousel"],
  email:     ["feed"],
  manual_export: ["feed"],
};

// Map matrix surface → model-level ``channel_format`` (feed | story | reel).
function surfaceToFormat(s) {
  const key = (s || "feed").toLowerCase();
  if (key === "story") return "story";
  if (["reel", "short", "long", "video_pin", "video_story"].includes(key)) {
    return "reel";
  }
  return "feed";  // feed, carousel, pin, photo, custom_thumbnail
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return String(iso); }
}

/* -------------------------------------------------------------------------- */
/*  Composer                                                                  */
/* -------------------------------------------------------------------------- */

export default function PublishCompose() {
  const nav = useNavigate();

  const [step, setStep] = useState(1); // 1 pick, 2 configure, 3 preflight, 4 launch
  const [assetSets, setAssetSets] = useState([]);
  const [connections, setConnections] = useState([]);
  const [channelsCatalog, setChannelsCatalog] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [loadState, setLoadState] = useState({ loading: true, error: null });

  // Selection
  const [selectedSetId, setSelectedSetId] = useState("");
  const [channel, setChannel] = useState("facebook");
  const [connectionId, setConnectionId] = useState("");
  const [pageMessage, setPageMessage] = useState("");
  // P2 · Pinterest board picker.
  const [pinterestBoards, setPinterestBoards] = useState([]);
  const [pinterestBoardsCacheInfo, setPinterestBoardsCacheInfo] = useState(null);
  const [pinterestBoardsError, setPinterestBoardsError] = useState(null);
  const [pinterestBoardId, setPinterestBoardId] = useState("");
  const [pinterestBoardTitle, setPinterestBoardTitle] = useState("");
  const [pinterestBoardDesc, setPinterestBoardDesc] = useState("");
  const [pinterestBoardsBusy, setPinterestBoardsBusy] = useState(false);
  const [subject, setSubject] = useState("");
  const [toEmails, setToEmails] = useState("");
  const [testMode, setTestMode] = useState(true);
  // P5.4 (2026-11-03) · Item 2 — Acceptance-test flag flows onto
  // the plan + receipt so Roger can filter the Queue for acceptance
  // runs and the one-click `/delete-from-platform` button appears.
  const [isAcceptanceTest, setIsAcceptanceTest] = useState(false);
  const [note, setNote] = useState("");
  // 2026-10-26 — Stories/Reels publishing surfaces.
  const [alsoStory, setAlsoStory] = useState(false);
  const [storyImageUrl, setStoryImageUrl] = useState("");
  const [channelFormat, setChannelFormat] = useState("feed"); // feed | story | reel
  const [reelVideoUrl, setReelVideoUrl] = useState("");
  const [reelCaption, setReelCaption] = useState("");

  // Result state
  const [plan, setPlan] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);

  // The currently-selected asset set row (used to detect frozen reel
  // sets and to prefill the Reel surface read-only).
  const selectedSet = useMemo(
    () => assetSets.find((s) => s.id === selectedSetId) || null,
    [assetSets, selectedSetId],
  );
  const selectedIsReelSet = selectedSet && selectedSet.kind === "reel";

  // P3 — currently-targeted publish surface, formatted the way the
  // reel-cut resolver + adapter registry key them. ``feed`` collapses
  // to the bare channel (``facebook``, ``tiktok``). Everything else is
  // ``channel:format`` (``instagram:story``, ``facebook:reel``).
  const activeSurfaceKey = useMemo(() => {
    if (!channel) return "";
    if (!channelFormat || channelFormat === "feed") return channel;
    return `${channel}:${channelFormat}`;
  }, [channel, channelFormat]);

  // Resolved cut for the active surface, from the server-side auto-pick
  // + overrides map embedded on the frozen reel asset set.
  const resolvedCutForSurface = useMemo(() => {
    if (!selectedIsReelSet) return null;
    const map = selectedSet.resolved_video_url_by_surface || {};
    return map[activeSurfaceKey] || null;
  }, [selectedIsReelSet, selectedSet, activeSurfaceKey]);

  const [cutOverrideBusy, setCutOverrideBusy] = useState(false);

  // Load asset sets + connections + channel catalog up front.
  const load = useCallback(async () => {
    setLoadState({ loading: true, error: null });
    try {
      const [r_sets, r_conns, r_channels, r_approvals] = await Promise.all([
        api.get("/asset-sets/publishable"),
        api.get("/publish/channel-connections"),
        api.get("/publish/channels").catch(() => ({ data: { channels: [] } })),
        api.get("/platform-approvals").catch(() => ({ data: { approvals: [] } })),
      ]);
      setAssetSets(r_sets.data.asset_sets || []);
      setConnections(r_conns.data.connections || []);
      setChannelsCatalog(r_channels.data.channels || []);
      setApprovals(r_approvals.data.approvals || []);
      setLoadState({ loading: false, error: null });
    } catch (e) {
      setLoadState({ loading: false, error: formatApiError(e) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Build the channel options dynamically from the server's catalog +
  // the always-on list. A channel appears if it has credentials
  // configured OR it's always-on (manual_export). This covers the
  // Instagram-HEALTHY-but-dropdown-missing defect: once /publish/channels
  // reports credentials_configured=true, Instagram shows up here.
  const channelOptions = useMemo(() => {
    const configured = new Set(
      channelsCatalog
        .filter((c) => c.credentials_configured)
        .map((c) => c.channel),
    );
    const union = new Set([...configured, ...ALWAYS_ON_CHANNELS]);
    const list = Array.from(union).map((ch) => ({
      value: ch,
      label: CHANNEL_LABELS[ch] || ch,
      health: (channelsCatalog.find((c) => c.channel === ch) || {}).health,
    }));
    // Deterministic order so the dropdown doesn't shuffle between loads.
    const order = ["facebook", "instagram", "email", "youtube",
                   "tiktok", "pinterest", "manual_export"];
    list.sort(
      (a, b) => order.indexOf(a.value) - order.indexOf(b.value),
    );
    return list;
  }, [channelsCatalog]);

  // When a reel asset set is picked, auto-switch Surface to reel and
  // prefill video_url + caption from the frozen reel (both read-only).
  // P3 — ``video_url`` now reflects the per-surface resolved cut
  // (full / 30s / 15s) with owner-override honoured.
  useEffect(() => {
    if (selectedIsReelSet) {
      setChannelFormat("reel");
      setAlsoStory(false);
      // Default to the resolver's pick for whichever surface the
      // operator ends up targeting. If the resolver errored for this
      // surface we fall back to the top-level ``video_url`` and let
      // the composer render the error inline.
      const picked =
        (selectedSet.resolved_video_url_by_surface || {})[
          channel && channelFormat && channelFormat !== "feed"
            ? `${channel}:${channelFormat}`
            : channel || "facebook:reel"
        ] || null;
      const url = (picked && picked.video_url) || selectedSet.video_url || "";
      setReelVideoUrl(url);
      setReelCaption(selectedSet.caption || "");
      setPageMessage(selectedSet.caption || "");
      // Reel plans go to Meta surfaces; default to the one with an
      // ACTIVE connection, falling back to facebook.
      if (!["facebook", "instagram"].includes(channel)) {
        setChannel("facebook");
      }
    }
    // intentionally no cleanup on deselect — the composer resets on
    // reload; local state staying prefilled is fine.
     
  }, [selectedIsReelSet, selectedSet?.id]);

  // When the operator changes channel or surface AFTER picking a reel
  // set, re-resolve the cut so ``reelVideoUrl`` tracks the surface.
  useEffect(() => {
    if (!selectedIsReelSet) return;
    const picked = resolvedCutForSurface;
    if (picked && picked.video_url) {
      setReelVideoUrl(picked.video_url);
    }
     
  }, [selectedIsReelSet, resolvedCutForSurface && resolvedCutForSurface.video_url]);

  // Round 21 (2026-11-02) — imported reels carry one approved caption
  // PER CHANNEL. When the operator changes channel, show the caption
  // that was approved for that channel (read-only; the server prefills
  // the same text, so what ships is exactly what was approved).
  const importedChannelKey =
    channel === "facebook" ? "facebook_reel"
    : channel === "instagram" ? "instagram_reel"
    : channel === "youtube" ? "youtube_short"
    : channel === "pinterest" ? "pinterest_video_pin" : null;
  const importedCaption = (selectedIsReelSet && selectedSet.channel_captions && importedChannelKey)
    ? selectedSet.channel_captions[importedChannelKey] || null
    : null;
  useEffect(() => {
    if (!importedCaption) return;
    const text = importedCaption.caption
      || [importedCaption.title, importedCaption.description].filter(Boolean).join("\n");
    setReelCaption(text);
    setPageMessage(text);
    if (channel === "pinterest") {
      setPinterestBoardTitle(importedCaption.title || "");
      setPinterestBoardDesc(importedCaption.description || "");
    }
     
  }, [channel, selectedIsReelSet, selectedSet?.id]);
  const isWellSet = /\bwell\b/i.test(selectedSet?.product_name || "");

  // Filter connections to the current channel.
  //  * applicableConns  — ACTIVE connections eligible for a publish.
  //  * reauthConns      — connections that match the channel but need
  //                       reauth (REAUTH_REQUIRED / EXPIRED / INVALID).
  //                       Still surfaced so Roger can click Reconnect
  //                       instead of thinking the channel is missing.
  const applicableConns = useMemo(
    () => connections.filter((c) => c.channel === channel && c.status === "ACTIVE"),
    [connections, channel],
  );
  const reauthConns = useMemo(
    () => connections.filter((c) =>
      c.channel === channel &&
      ["REAUTH_REQUIRED", "EXPIRED", "INVALID"].includes(
        (c.health || "").toUpperCase(),
      )
    ),
    [connections, channel],
  );

  // Auto-pick the default active connection for the selected channel
  useEffect(() => {
    if (!connectionId && applicableConns.length > 0) {
      const dflt = applicableConns.find((c) => c.is_default) || applicableConns[0];
      setConnectionId(dflt.id);
    }
    if (connectionId && !applicableConns.some((c) => c.id === connectionId)) {
      setConnectionId("");
    }
  }, [channel, applicableConns, connectionId]);

  const selectedConnection = applicableConns.find((c) => c.id === connectionId);

  async function handleCreatePlan() {
    setBusy(true); setError(null);
    try {
      const channel_config = { test_mode: testMode };
      if (channel === "facebook") channel_config.page_message = pageMessage;
      if (channel === "pinterest") {
        // Board picker selection (default board is auto-filled server-side
        // when this is empty). Title/description travel with it.
        if (pinterestBoardId) channel_config.board_id = pinterestBoardId;
        if (pinterestBoardTitle.trim()) channel_config.title = pinterestBoardTitle.trim();
        if (pinterestBoardDesc.trim()) channel_config.description = pinterestBoardDesc.trim();
        if (selectedIsReelSet) channel_config.pin_type = "video";
      }
      if (channel === "email") {
        channel_config.subject = subject;
        channel_config.to = toEmails.split(",").map((s) => s.trim()).filter(Boolean);
      }
      // Stories: image_url picked up by both FB and IG story adapters.
      if (alsoStory && storyImageUrl.trim()) {
        channel_config.story_image_url = storyImageUrl.trim();
      }
      // Reels: this plan IS the reel; adapters read video_url + caption.
      const isReel = channelFormat === "reel";
      if (isReel) {
        channel_config.video_url = reelVideoUrl.trim();
        channel_config.caption = reelCaption.trim();
      }
      const r = await api.post(
        `/asset-sets/${selectedSetId}/publish-plans`,
        {
          channel,
          channel_connection_id: connectionId || undefined,
          channel_config,
          channel_format: channelFormat,
          also_story: !isReel && alsoStory, // sibling only on feed
          is_acceptance_test: isAcceptanceTest,
        },
      );
      setPlan(r.data);
      setStep(3);
    } catch (e) {
      // 409 = a non-terminal plan already exists for (asset_set, channel).
      // The backend returns the existing plan id in the error detail so
      // we can auto-resume without a separate list endpoint.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || {};
      const existingId = detail.existing_plan_id;
      if (status === 409 && existingId) {
        try {
          const r2 = await api.get(`/publish-plans/${existingId}`);
          setPlan(r2.data);
          setStep(3);
          setError(null);
          setFlash(
            `A draft plan already exists for this asset set + channel. Resumed it (id ${existingId.slice(0, 8)}…).`,
          );
          return;
        } catch (_) { /* fall through to generic error */ }
      }
      setError(formatApiError(e));
    } finally { setBusy(false); }
  }

  async function handlePreflight() {
    setBusy(true); setError(null);
    try {
      const r = await api.post(`/publish-plans/${plan.id}/preflight`, {});
      setPreflight(r.data);
      if (r.data.overall === "PASS") setStep(4);
    } catch (e) {
      setError(formatApiError(e));
    } finally { setBusy(false); }
  }

  async function handlePublishNow() {
    setBusy(true); setError(null);
    try {
      const r = await api.post(`/publish-plans/${plan.id}/publish`);
      setFlash(`Published! Receipt: ${r.data.receipt_id?.slice(0, 8) || "…"}`);
      setTimeout(() => nav(`/brand-os/publish/queue`), 1200);
    } catch (e) {
      setError(formatApiError(e));
    } finally { setBusy(false); }
  }

  async function handleSchedule(iso) {
    setBusy(true); setError(null);
    try {
      const r = await api.post(`/publish-plans/${plan.id}/schedules`, {
        scheduled_for: iso, note: note || undefined,
      });
      setFlash(`Scheduled for ${fmtDateTime(r.data.scheduled_for)}.`);
      setTimeout(() => nav(`/brand-os/publish/queue`), 1200);
    } catch (e) {
      setError(formatApiError(e));
    } finally { setBusy(false); }
  }

  if (loadState.loading) return (
    <Shell title="New Publish"><div className="bo-page"><LoadingCard label="Loading composer…" /></div></Shell>
  );

  return (
    <Shell title="New Publish">
      <div className="bo-page" data-testid="publish-compose-page">
        <section className="bo-card">
          <div className="bo-eyebrow">Publish</div>
          <h1 className="bo-serif" style={{ fontSize: 26, margin: "6px 0 4px" }}>New publish</h1>
          <p style={{ color: "var(--bo-muted)", maxWidth: 720, lineHeight: 1.55, marginTop: 4 }}>
            Pick an approved asset set, choose a channel + connection, run preflight,
            then publish now or schedule for later. Every step is auditable via a
            Publish Receipt.
          </p>
          <ErrorBanner error={loadState.error || error} testId="publish-compose-error" />
          {flash && (
            <div className="bo-flash" style={{ marginTop: 12, padding: 10, borderRadius: 8, background: "var(--bo-accent-soft)", color: "var(--bo-ink)", fontSize: 13 }}>
              {flash}
            </div>
          )}
        </section>

        {/* --- Step 1: Pick asset set --- */}
        <section className="bo-card" style={{ marginTop: 20 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h2 className="bo-serif" style={{ fontSize: 18, margin: 0 }}>1 · Pick an approved asset set</h2>
            {selectedSetId && <Pill tone="success">Selected</Pill>}
          </div>
          {assetSets.length === 0 ? (
            <div style={{ marginTop: 12 }}>
              <EmptyState
                testId="publish-compose-no-sets"
                title="No approved asset sets yet"
                description="Approve at least one asset set before creating a publish plan."
              />
            </div>
          ) : (
            <div style={{ marginTop: 12, display: "grid", gap: 8, maxHeight: 320, overflowY: "auto" }}>
              {assetSets.map((s) => (
                <label
                  key={s.id}
                  data-testid={`compose-set-${s.id}`}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "10px 12px", borderRadius: 8,
                    border: `1px solid ${selectedSetId === s.id ? "var(--bo-accent)" : "var(--bo-line)"}`,
                    background: selectedSetId === s.id ? "var(--bo-accent-soft)" : "var(--bo-panel)",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="radio"
                    name="asset-set"
                    checked={selectedSetId === s.id}
                    onChange={() => { setSelectedSetId(s.id); if (step === 1) setStep(2); }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", gap: 8,
                                  alignItems: "center" }}>
                      {s.kind === "reel" && (
                        <Pill tone="info" testId={`compose-set-${s.id}-kind`}>
                          Reel
                        </Pill>
                      )}
                      {s.provenance === "imported" && (
                        <Pill tone="warn" testId={`compose-set-${s.id}-imported`}>
                          Imported
                        </Pill>
                      )}
                      <span style={{ fontFamily: "var(--bo-serif)", fontSize: 15 }}>
                        {s.name}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: "var(--bo-muted)", marginTop: 2 }}>
                      {s.campaign_name || "(no campaign)"} · approved {fmtDateTime(s.approved_at)}
                      {s.kind === "reel" && s.duration_s != null && (
                        <>
                          {" · "}
                          <span data-testid={`compose-set-${s.id}-duration`}>
                            {s.duration_s.toFixed(1)}s
                          </span>
                          {s.width && s.height && (
                            <> · {s.width}×{s.height}</>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </section>

        {/* --- Step 2: Configure --- */}
        {step >= 2 && (
          <section className="bo-card" style={{ marginTop: 20 }}>
            <h2 className="bo-serif" style={{ fontSize: 18, margin: 0 }}>2 · Configure the channel</h2>
            <div style={{ marginTop: 12, display: "grid", gap: 14 }}>
              {(() => {
                const ap = approvals.find((a) => a.channel === channel);
                if (!ap || !ap.requires_private_post) return null;
                return (
                  <div
                    className="bo-hint"
                    data-testid={`compose-approval-banner-${channel}`}
                    style={{
                      padding: "10px 12px", borderRadius: 6,
                      background: "var(--bo-warn-soft, #fff8e1)",
                      color: "var(--bo-ink, #333)",
                      fontSize: 13, lineHeight: 1.5,
                      border: "1px solid var(--bo-warn, #d9a94e)",
                    }}
                  >
                    <strong style={{ marginRight: 6 }}>
                      Approval pending ({ap.status}).
                    </strong>
                    {ap.banner}
                    {ap.owner_note && (
                      <div style={{ marginTop: 4, fontStyle: "italic",
                                      color: "var(--bo-muted)" }}>
                        Owner note: {ap.owner_note}
                      </div>
                    )}
                  </div>
                );
              })()}
              <div className="bo-field">
                <label className="bo-label">Channel</label>
                <select
                  className="bo-input"
                  data-testid="compose-channel"
                  value={channel}
                  onChange={(e) => setChannel(e.target.value)}
                  disabled={selectedIsReelSet
                    && !["facebook", "instagram"].includes(channel)
                    && channelOptions.some(
                      (o) => ["facebook", "instagram"].includes(o.value),
                    )}
                >
                  {channelOptions.length === 0 ? (
                    <option value="" disabled>
                      No channels configured yet
                    </option>
                  ) : (
                    channelOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                        {o.health && o.health !== "HEALTHY"
                          ? ` · ${o.health}`
                          : ""}
                      </option>
                    ))
                  )}
                </select>
                {selectedIsReelSet && (
                  <div
                    className="bo-hint"
                    data-testid="compose-reel-channel-hint"
                    style={{ fontSize: 12, color: "var(--bo-muted)",
                             marginTop: 4 }}
                  >
                    This is a frozen reel. It can go to Facebook Reels,
                    Instagram Reels, YouTube Shorts, Pinterest video pins
                    and TikTok; feed-only channels will not accept it.
                  </div>
                )}
              </div>
              {channel !== "manual_export" && (
                <div className="bo-field">
                  <label className="bo-label">Channel connection</label>
                  {applicableConns.length === 0 ? (
                    <div
                      className="bo-hint"
                      style={{ color: "var(--bo-danger)" }}
                      data-testid="compose-no-active-connection"
                    >
                      No ACTIVE connection for this channel.{" "}
                      {reauthConns.length > 0 ? (
                        <>
                          Found {reauthConns.length}{" "}
                          {reauthConns.length === 1 ? "connection" : "connections"}{" "}
                          that need reauth:{" "}
                          {reauthConns.map((c, i) => (
                            <span key={c.id}>
                              <a
                                href="/brand-os/admin/channel-connections"
                                data-testid={`compose-reconnect-${c.id}`}
                              >
                                Reconnect {c.display_name || c.channel}
                              </a>
                              {i < reauthConns.length - 1 ? ", " : ""}
                            </span>
                          ))}
                          .
                        </>
                      ) : (
                        <>
                          Add one at{" "}
                          <a href="/brand-os/admin/channel-connections">
                            Channel Connections
                          </a>.
                        </>
                      )}
                    </div>
                  ) : (
                    <select
                      className="bo-input"
                      data-testid="compose-connection"
                      value={connectionId}
                      onChange={(e) => setConnectionId(e.target.value)}
                    >
                      {applicableConns.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.display_name} {c.is_default ? "(default)" : ""} · {c.health}
                        </option>
                      ))}
                    </select>
                  )}
                  {selectedConnection && selectedConnection.health === "UNKNOWN" && (
                    <div
                      className="bo-hint"
                      data-testid="compose-connection-unknown-explainer"
                      style={{ color: "var(--bo-muted)" }}
                    >
                      ℹ️ Health <strong>UNKNOWN</strong> just means this connection
                      hasn't been ping-validated against the provider yet. Preflight
                      step 6 (channel_validation) will validate it and flip health
                      to HEALTHY on success — no action needed from you first.
                    </div>
                  )}
                  {selectedConnection && selectedConnection.health !== "HEALTHY" && selectedConnection.health !== "UNKNOWN" && (
                    <div className="bo-hint" style={{ color: "var(--bo-danger)" }}>
                      ⚠️ This connection is <strong>{selectedConnection.health}</strong>. The scheduler will pause any action bound to it until you rotate the secret or reconnect.
                    </div>
                  )}
                </div>
              )}
              {channel === "facebook" && (
                <div className="bo-field">
                  <label className="bo-label">Post message</label>
                  <textarea
                    className="bo-input"
                    data-testid="compose-fb-message"
                    rows={4}
                    value={pageMessage}
                    onChange={(e) => setPageMessage(e.target.value)}
                    placeholder="What should this post say on your Facebook Page?"
                    required
                  />
                </div>
              )}
              {channel === "email" && (
                <>
                  <div className="bo-field">
                    <label className="bo-label">Subject</label>
                    <input className="bo-input" data-testid="compose-email-subject" value={subject} onChange={(e) => setSubject(e.target.value)} required />
                  </div>
                  <div className="bo-field">
                    <label className="bo-label">To (comma-separated)</label>
                    <input className="bo-input" data-testid="compose-email-to" value={toEmails} onChange={(e) => setToEmails(e.target.value)} placeholder="alice@example.com, bob@example.com" required />
                  </div>
                </>
              )}
              {channel === "pinterest" && (
                <PinterestBoardPickerSection
                  key={connectionId}
                  connection={selectedConnection}
                  approval={approvals.find((a) => a.channel === "pinterest")}
                  boards={pinterestBoards}
                  cacheInfo={pinterestBoardsCacheInfo}
                  error={pinterestBoardsError}
                  busy={pinterestBoardsBusy}
                  boardId={pinterestBoardId}
                  title={pinterestBoardTitle}
                  description={pinterestBoardDesc}
                  onBoardIdChange={setPinterestBoardId}
                  onTitleChange={setPinterestBoardTitle}
                  onDescriptionChange={setPinterestBoardDesc}
                  onLoad={async (opts) => {
                    if (!connectionId) {
                      setPinterestBoardsError("Pick a Pinterest account first.");
                      return;
                    }
                    setPinterestBoardsBusy(true);
                    setPinterestBoardsError(null);
                    try {
                      const r = await api.get("/publish/pinterest/boards", {
                        params: {
                          connection_id: connectionId,
                          refresh: !!opts?.refresh,
                        },
                      });
                      const loadedBoards = r.data.boards || [];
                      setPinterestBoards(loadedBoards);
                      // Round 21 — default board: for a Well video prefer the
                      // board named "The Well by Precipice Health" when it
                      // exists; otherwise leave the server default in place.
                      if (!pinterestBoardId && isWellSet) {
                        const wellBoard = loadedBoards.find((b) =>
                          /the well by precipice health/i.test(b.name || ""));
                        if (wellBoard) setPinterestBoardId(wellBoard.id);
                      }
                      setPinterestBoardsCacheInfo({
                        cached_at: r.data.cached_at,
                        cache_hit: r.data.cache_hit,
                        cache_expires_at: r.data.cache_expires_at,
                        sandbox: r.data.sandbox,
                      });
                    } catch (e) {
                      setPinterestBoardsError(
                        e?.response?.data?.detail?.message || e.message,
                      );
                    } finally {
                      setPinterestBoardsBusy(false);
                    }
                  }}
                />
              )}
              <label className="bo-check" style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" data-testid="compose-test-mode" checked={testMode} onChange={(e) => setTestMode(e.target.checked)} />
                <span>Test mode — short-circuit the external call (safe smoke)</span>
              </label>
              <label className="bo-check" style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" data-testid="compose-is-acceptance-test"
                       checked={isAcceptanceTest}
                       onChange={(e) => setIsAcceptanceTest(e.target.checked)} />
                <span>Mark as acceptance test — the receipt gets a one-click <strong>delete-from-platform</strong> button</span>
              </label>
              {/* 2026-10-26 — Stories + Reels controls (Meta channels only).
                  Feed is the default; a feed post can carry an also_story
                  sibling; a reel is its own channel_format. When the
                  selected asset set is a FROZEN REEL, the Reel radio is
                  forced on and the Feed radio is disabled — the Guardian
                  gate already signed off the reel surfaces, so a feed
                  post from a reel source would mix governance states. */}
              {(channel === "facebook" || channel === "instagram") && (
                <div
                  className="bo-field"
                  data-testid="compose-stories-reels-controls"
                  style={{ display: "flex", flexDirection: "column", gap: 10,
                           borderTop: "1px dashed var(--bo-border, #ddd)",
                           paddingTop: 10 }}
                >
                  <label className="bo-label" style={{ marginBottom: 0 }}>
                    Surface
                  </label>
                  <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                    <label style={{ display: "flex", alignItems: "center",
                                     gap: 6,
                                     cursor: selectedIsReelSet ? "not-allowed" : "pointer",
                                     opacity: selectedIsReelSet ? 0.5 : 1 }}>
                      <input
                        type="radio"
                        name="compose-channel-format"
                        data-testid="compose-channel-format-feed"
                        checked={channelFormat === "feed"}
                        onChange={() => setChannelFormat("feed")}
                        disabled={selectedIsReelSet}
                      />
                      <span>Feed post</span>
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                      <input
                        type="radio"
                        name="compose-channel-format"
                        data-testid="compose-channel-format-reel"
                        checked={channelFormat === "reel"}
                        onChange={() => setChannelFormat("reel")}
                      />
                      <span>Reel (9:16 video)</span>
                    </label>
                  </div>
                  {channelFormat === "feed" && (
                    <>
                      <label
                        className="bo-check"
                        style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
                      >
                        <input
                          type="checkbox"
                          data-testid="compose-also-story"
                          checked={alsoStory}
                          onChange={(e) => setAlsoStory(e.target.checked)}
                        />
                        <span>
                          Also post as Story (sibling plan, same asset_set, same test-mode flag)
                        </span>
                      </label>
                      {alsoStory && (
                        <div className="bo-field" style={{ margin: 0 }}>
                          <label className="bo-label">
                            Story image URL (9:16 recommended; the publisher auto-fits to 1080×1920 with a blurred cover)
                          </label>
                          <input
                            className="bo-input"
                            data-testid="compose-story-image-url"
                            value={storyImageUrl}
                            onChange={(e) => setStoryImageUrl(e.target.value)}
                            placeholder="https://… (leave blank to reuse the feed image)"
                          />
                        </div>
                      )}
                    </>
                  )}
                  {channelFormat === "reel" && (
                    <>
                      <div className="bo-field" style={{ margin: 0 }}>
                        <label className="bo-label">
                          Reel video URL (mp4, 3–90 s, 9:16, H.264)
                          {selectedIsReelSet && (
                            <span
                              data-testid="compose-reel-video-url-readonly-badge"
                              style={{ marginLeft: 8, fontSize: 11,
                                       color: "var(--bo-muted)",
                                       fontStyle: "italic" }}
                            >
                              (from frozen reel · read-only)
                            </span>
                          )}
                        </label>
                        <input
                          className="bo-input"
                          data-testid="compose-reel-video-url"
                          value={reelVideoUrl}
                          onChange={(e) => setReelVideoUrl(e.target.value)}
                          placeholder="https://…/reel.mp4"
                          readOnly={selectedIsReelSet}
                          required
                        />
                      </div>
                      <div className="bo-field" style={{ margin: 0 }}>
                        <label className="bo-label">
                          Reel caption (Guardian-gated)
                          {selectedIsReelSet && (
                            <span
                              data-testid="compose-reel-caption-readonly-badge"
                              style={{ marginLeft: 8, fontSize: 11,
                                       color: "var(--bo-muted)",
                                       fontStyle: "italic" }}
                            >
                              {importedCaption
                                ? `(approved for ${CHANNEL_LABELS[channel] || channel} · read-only)`
                                : "(gated + signed off · read-only)"}
                            </span>
                          )}
                        </label>
                        <Textarea
                          data-testid="compose-reel-caption"
                          value={reelCaption}
                          onChange={(e) => setReelCaption(e.target.value)}
                          rows={3}
                          readOnly={selectedIsReelSet}
                          required
                        />
                      </div>

                      {/* P3 — Variant cut picker. Visible only when the
                          selected reel set has more than one cut
                          uploaded. Shows which cut was auto-picked for
                          the active surface + why, and lets an owner
                          pin a different cut via a one-tap override. */}
                      {selectedIsReelSet
                        && Array.isArray(selectedSet.cuts)
                        && selectedSet.cuts.length > 1 && (
                          <div
                            data-testid="compose-reel-cut-picker"
                            style={{
                              margin: 0, padding: 10,
                              border: "1px dashed var(--bo-line)",
                              borderRadius: 8,
                              background: "var(--bo-panel-alt, #faf8f2)",
                            }}
                          >
                            <div className="bo-eyebrow"
                                 style={{ fontSize: 10, marginBottom: 6 }}>
                              Reel cut · {activeSurfaceKey}
                            </div>
                            {resolvedCutForSurface && resolvedCutForSurface.error ? (
                              <div
                                data-testid="compose-reel-cut-error"
                                style={{
                                  padding: "6px 8px", borderRadius: 6,
                                  color: "var(--bo-danger, #b4332a)",
                                  background: "rgba(180,51,42,0.06)",
                                  border: "1px solid var(--bo-danger, #b4332a)",
                                  fontSize: 12, marginBottom: 8, lineHeight: 1.5,
                                }}
                              >
                                No uploaded cut fits <strong>{activeSurfaceKey}</strong>.
                                Attach a shorter cut from Creative Studio
                                before you create this plan.
                              </div>
                            ) : (
                              <div
                                data-testid="compose-reel-cut-resolved"
                                style={{
                                  fontSize: 12,
                                  color: "var(--bo-muted)",
                                  marginBottom: 8, lineHeight: 1.5,
                                }}
                              >
                                Publishing the <strong data-testid="compose-reel-cut-key">
                                  {resolvedCutForSurface?.cut_key || "full"}
                                </strong>{" "}
                                cut ·{" "}
                                <em data-testid="compose-reel-cut-source">
                                  {resolvedCutForSurface?.source || "auto"}
                                </em>{" "}
                                · {resolvedCutForSurface?.reason}
                              </div>
                            )}
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              {selectedSet.cuts.map((c) => {
                                const isActive =
                                  (resolvedCutForSurface?.cut_key || "") === c.cut_key;
                                return (
                                  <button
                                    key={c.cut_key}
                                    type="button"
                                    disabled={cutOverrideBusy || !selectedSet.reel_asset_id}
                                    data-testid={`compose-reel-cut-pick-${c.cut_key}`}
                                    onClick={async () => {
                                      if (!selectedSet.reel_asset_id) return;
                                      setCutOverrideBusy(true);
                                      try {
                                        const next = {
                                          ...(selectedSet.cut_overrides || {}),
                                          [activeSurfaceKey]: c.cut_key,
                                        };
                                        await api.put(
                                          `/creative/reels/${selectedSet.reel_asset_id}/cut-overrides`,
                                          { overrides: next },
                                        );
                                        setFlash({
                                          kind: "success",
                                          message: `Pinned ${c.cut_key} to ${activeSurfaceKey}.`,
                                        });
                                        await load();
                                      } catch (e) {
                                        setError(formatApiError(e));
                                      } finally {
                                        setCutOverrideBusy(false);
                                      }
                                    }}
                                    style={{
                                      padding: "6px 10px", borderRadius: 6,
                                      border: isActive
                                        ? "1px solid var(--bo-primary)"
                                        : "1px solid var(--bo-line)",
                                      background: isActive
                                        ? "var(--bo-primary-soft, #f1ece0)"
                                        : "white",
                                      fontWeight: isActive ? 600 : 400,
                                      cursor: cutOverrideBusy
                                        ? "wait" : "pointer",
                                      fontSize: 12,
                                    }}
                                  >
                                    {c.cut_key}
                                    {c.probe?.duration_s != null && (
                                      <span style={{
                                        marginLeft: 6, fontSize: 10,
                                        color: "var(--bo-muted)",
                                      }}>
                                        {c.probe.duration_s.toFixed(1)}s
                                      </span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                    </>
                  )}
                </div>
              )}
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button
                  className="bo-btn bo-btn-primary"
                  data-testid="compose-create-plan"
                  onClick={handleCreatePlan}
                  disabled={busy || !selectedSetId
                    || (channel === "facebook" && channelFormat === "feed" && (!pageMessage.trim() || !connectionId))
                    || (channel === "email" && (!subject.trim() || !toEmails.trim() || !connectionId))
                    || (channelFormat === "reel" && (!reelVideoUrl.trim() || !reelCaption.trim()))}
                >
                  {busy ? "Creating…" : plan ? "Recreate plan" : "Create plan →"}
                </button>
              </div>
            </div>
          </section>
        )}

        {/* --- Step 3: Preflight --- */}
        {step >= 3 && plan && (
          <section className="bo-card" style={{ marginTop: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 className="bo-serif" style={{ fontSize: 18, margin: 0 }}>3 · Preflight</h2>
              <span className="bo-mono" style={{ fontSize: 12, color: "var(--bo-muted)" }}>plan {plan.id.slice(0, 8)}</span>
            </div>
            {!preflight ? (
              <div style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button className="bo-btn bo-btn-primary" data-testid="compose-run-preflight" onClick={handlePreflight} disabled={busy}>
                  {busy ? "Running…" : "Run preflight"}
                </button>
              </div>
            ) : (
              <>
                <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <Pill
                    tone={preflight.overall === "PASS" ? "success" : preflight.overall === "WARN" ? "warn" : "danger"}
                    testId="compose-preflight-verdict"
                  >
                    Overall: {preflight.overall}
                  </Pill>
                  {preflight.overall_summary && (
                    <span style={{ fontSize: 13, color: "var(--bo-muted)" }}
                          data-testid="compose-preflight-summary">
                      {preflight.overall_summary}
                    </span>
                  )}
                  <button
                    className="bo-btn bo-btn-ghost"
                    data-testid="compose-rerun-preflight"
                    onClick={handlePreflight}
                    disabled={busy}
                    style={{ marginLeft: "auto" }}
                  >
                    {busy ? "Running…" : "Re-run preflight"}
                  </button>
                </div>

                <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
                  {(preflight.steps || []).map((s, i) => (
                    <PreflightStepCard key={`${s.step}-${i}`} step={s} />
                  ))}
                </div>

                {preflight.overall === "BLOCK" && (
                  <div
                    className="bo-hint"
                    data-testid="compose-preflight-block-explainer"
                    style={{
                      marginTop: 14, padding: "10px 12px", borderRadius: 6,
                      background: "var(--bo-danger-soft)",
                      color: "var(--bo-danger)", fontSize: 13,
                    }}
                  >
                    <strong>BLOCK cannot be overridden.</strong> Fix the
                    underlying findings (each has a remediation + link
                    above), re-approve the asset set if needed, then
                    click <em>Re-run preflight</em>.
                  </div>
                )}
                {preflight.overall === "WARN" && (
                  <div
                    className="bo-hint"
                    data-testid="compose-preflight-warn-explainer"
                    style={{
                      marginTop: 14, padding: "10px 12px", borderRadius: 6,
                      background: "var(--bo-warn-soft)",
                      color: "var(--bo-warn)", fontSize: 13,
                    }}
                  >
                    <strong>WARN can be overridden</strong> by an owner or
                    admin with a written note. Continue to step 4 to
                    proceed with an override.
                  </div>
                )}
              </>
            )}
          </section>
        )}

        {/* --- Step 4: Launch --- */}
        {step >= 4 && plan && preflight?.overall === "PASS" && (
          <section className="bo-card" style={{ marginTop: 20 }}>
            <h2 className="bo-serif" style={{ fontSize: 18, margin: 0 }}>4 · Publish now or schedule</h2>
            <div style={{ marginTop: 12, display: "grid", gap: 14 }}>
              <div className="bo-field">
                <label className="bo-label">Note (optional)</label>
                <input className="bo-input" data-testid="compose-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Short annotation for the queue" />
              </div>
              <ScheduleLauncher
                onPublishNow={handlePublishNow}
                onSchedule={handleSchedule}
                busy={busy}
              />
            </div>
          </section>
        )}
      </div>
    </Shell>
  );
}

function ScheduleLauncher({ onPublishNow, onSchedule, busy }) {
  const [when, setWhen] = useState(() => {
    const d = new Date(Date.now() + 15 * 60 * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  function handleSchedule() {
    const iso = new Date(when).toISOString();
    onSchedule(iso);
  }
  return (
    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
      <button
        className="bo-btn bo-btn-primary"
        data-testid="compose-publish-now"
        onClick={onPublishNow}
        disabled={busy}
      >
        {busy ? "Publishing…" : "Publish now"}
      </button>
      <div className="bo-field" style={{ flex: "1 1 260px", margin: 0 }}>
        <label className="bo-label">Or schedule for</label>
        <input
          className="bo-input"
          data-testid="compose-schedule-when"
          type="datetime-local"
          value={when}
          onChange={(e) => setWhen(e.target.value)}
        />
      </div>
      <button
        className="bo-btn bo-btn-ghost"
        data-testid="compose-schedule-submit"
        onClick={handleSchedule}
        disabled={busy || !when}
      >
        Schedule
      </button>
    </div>
  );
}


// ---------------------------------------------------------------------------
// PreflightStepCard
//
// Renders one of the 6 Preflight steps with full operator visibility:
//  - verdict pill (PASS / WARN / BLOCK)
//  - plain-English summary of what the step checks
//  - per-finding rows with rule, severity, message, and remediation
//  - deep_link buttons that jump the operator straight to the fix
// ---------------------------------------------------------------------------
function PreflightStepCard({ step }) {
  const verdict = (step?.verdict || "PASS").toUpperCase();
  const tone = verdict === "PASS" ? "success" : verdict === "WARN" ? "warn" : "danger";
  const findings = [
    ...((step?.detail?.text_findings) || []).map((f) => ({ ...f, _kind: "text" })),
    ...((step?.detail?.image_findings) || []).map((f) => ({ ...f, _kind: "image" })),
  ];
  const hasFindings = findings.length > 0;
  const stepLabel = (step?.step || "step")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div
      data-testid={`preflight-step-${step?.step}`}
      className="bo-card"
      style={{
        padding: 12,
        borderLeft: `3px solid ${
          verdict === "PASS" ? "var(--bo-success)"
            : verdict === "WARN" ? "var(--bo-warn)"
              : "var(--bo-danger)"
        }`,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <Pill tone={tone} testId={`preflight-step-verdict-${step?.step}`}>
          {verdict}
        </Pill>
        <span style={{ fontFamily: "var(--bo-serif)", fontSize: 15 }}>{stepLabel}</span>
      </div>
      {step?.summary && (
        <div
          data-testid={`preflight-step-summary-${step?.step}`}
          style={{ marginTop: 6, fontSize: 13, color: "var(--bo-muted)" }}
        >
          {step.summary}
        </div>
      )}
      {step?.remediation && verdict !== "PASS" && (
        <div
          data-testid={`preflight-step-remediation-${step?.step}`}
          style={{
            marginTop: 8,
            padding: "8px 10px",
            borderRadius: 6,
            background: "var(--bo-panel-sunken)",
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <strong style={{ marginRight: 6 }}>What to do:</strong>
          <span>{step.remediation}</span>
          {step.deep_link && (
            <a
              href={step.deep_link}
              data-testid={`preflight-step-deeplink-${step?.step}`}
              style={{ marginLeft: 8, color: "var(--bo-accent)" }}
            >
              → Go fix it
            </a>
          )}
        </div>
      )}

      {hasFindings && (
        <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
          {findings.map((f, i) => (
            <PreflightFindingRow
              key={`${step?.step}-f-${i}`}
              finding={f}
              stepName={step?.step}
              index={i}
            />
          ))}
        </div>
      )}

      {/* Fallback: pretty-print detail block for steps whose findings
          don't live under text_findings/image_findings (channel_validation,
          budget, strategy_integrity). Only shows when non-PASS. */}
      {!hasFindings && verdict !== "PASS" && step?.detail && (
        <details
          data-testid={`preflight-step-raw-${step?.step}`}
          style={{ marginTop: 10 }}
        >
          <summary style={{ cursor: "pointer", fontSize: 13, color: "var(--bo-muted)" }}>
            Raw detail (for advanced debugging)
          </summary>
          <pre
            className="bo-mono"
            style={{
              marginTop: 6, padding: 10, borderRadius: 6,
              background: "var(--bo-panel-sunken)", fontSize: 11,
              overflow: "auto", maxHeight: 220,
            }}
          >
            {JSON.stringify(step.detail, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

function PreflightFindingRow({ finding, stepName, index }) {
  const sev = (finding?.severity || "").toUpperCase();
  const tone = sev === "BLOCK" ? "danger" : sev === "WARN" ? "warn" : "muted";
  const testKey = `preflight-finding-${stepName}-${index}`;
  return (
    <div
      data-testid={testKey}
      style={{
        padding: "10px 12px",
        borderRadius: 6,
        background: "var(--bo-panel-sunken)",
        borderLeft: `3px solid ${
          sev === "BLOCK" ? "var(--bo-danger)"
            : sev === "WARN" ? "var(--bo-warn)"
              : "var(--bo-line)"
        }`,
        display: "grid", gap: 6,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Pill tone={tone} testId={`${testKey}-severity`}>{sev || "INFO"}</Pill>
        <span className="bo-mono" style={{ fontSize: 12, color: "var(--bo-muted)" }}>
          {finding?.rule || "(rule)"}
        </span>
      </div>
      {finding?.message && (
        <div style={{ fontSize: 13 }} data-testid={`${testKey}-message`}>
          {finding.message}
        </div>
      )}
      {finding?.statement_preview && (
        <div
          data-testid={`${testKey}-statement-preview`}
          style={{
            fontSize: 13, fontStyle: "italic",
            padding: "6px 10px", background: "var(--bo-panel)",
            borderRadius: 4,
          }}
        >
          Required text: “{finding.statement_preview}”
          {finding.statement_field_path && (
            <span style={{ color: "var(--bo-muted)", marginLeft: 6, fontStyle: "normal", fontSize: 12 }}>
              (from Brand Vault: {finding.statement_field_path})
            </span>
          )}
        </div>
      )}
      {typeof finding?.length === "number" && typeof finding?.cap === "number" && (
        <div
          data-testid={`${testKey}-length-meter`}
          style={{ fontSize: 12, color: "var(--bo-muted)" }}
        >
          {finding.length} / {finding.cap} chars
          <div
            style={{
              marginTop: 4, height: 4, borderRadius: 2,
              background: "var(--bo-line)", overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${Math.min(100, (finding.length / finding.cap) * 100)}%`,
                height: "100%",
                background: sev === "BLOCK" ? "var(--bo-danger)"
                  : sev === "WARN" ? "var(--bo-warn)" : "var(--bo-success)",
              }}
            />
          </div>
        </div>
      )}
      {finding?.remediation && (
        <div
          data-testid={`${testKey}-remediation`}
          style={{ fontSize: 13, lineHeight: 1.5 }}
        >
          <strong style={{ marginRight: 4 }}>Fix:</strong>
          <span>{finding.remediation}</span>
          {finding.deep_link && (
            <a
              href={finding.deep_link}
              data-testid={`${testKey}-deeplink`}
              style={{ marginLeft: 8, color: "var(--bo-accent)" }}
            >
              → Open
            </a>
          )}
        </div>
      )}
    </div>
  );
}


/**
 * P2 · Pinterest-specific config section inside the composer.
 *
 * Renders:
 *   - a Trial/Standard access badge sourced from the platform
 *     approval's ``last_api_evidence.access_tier``;
 *   - a board picker populated from
 *     ``GET /publish/pinterest/boards?connection_id=...``, cached 1h
 *     server-side with a refresh button that bypasses the cache;
 *   - title / description inputs (deterministic 100 / 500 char caps
 *     per Pinterest's rules).
 *
 * The parent owns connection selection + board state; this component
 * is purely presentational + a load/refresh callback.
 */
function PinterestBoardPickerSection({
  connection, approval, boards, cacheInfo, error, busy,
  boardId, title, description,
  onBoardIdChange, onTitleChange, onDescriptionChange, onLoad,
}) {
  // Trial vs Standard tier from the platform_approval evidence.
  const accessTier = (
    approval?.last_api_evidence?.access_tier || "trial"
  ).toLowerCase();
  const tierBadge = accessTier === "standard" || accessTier === "production" ? (
    <span
      data-testid="pinterest-access-tier"
      style={{
        padding: "2px 8px", borderRadius: 999, fontSize: 11,
        background: "#e8f0ff", color: "#1b4bbf", fontWeight: 600,
      }}
    >
      Standard access
    </span>
  ) : (
    <span
      data-testid="pinterest-access-tier"
      style={{
        padding: "2px 8px", borderRadius: 999, fontSize: 11,
        background: "#fff2d9", color: "#8a5a00", fontWeight: 600,
      }}
    >
      Trial access · pins visible only to you
    </span>
  );

  return (
    <div
      data-testid="compose-pinterest-section"
      style={{ display: "flex", flexDirection: "column", gap: 10,
                borderTop: "1px dashed var(--bo-border, #ddd)",
                paddingTop: 10 }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <label className="bo-label" style={{ marginBottom: 0 }}>
          Pinterest board
        </label>
        {tierBadge}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        <select
          className="bo-input"
          data-testid="compose-pinterest-board"
          value={boardId}
          onChange={(e) => onBoardIdChange(e.target.value)}
          style={{ flex: 1 }}
        >
          <option value="">
            {boards.length === 0
              ? "Load boards to pick one…"
              : "Select a board…"}
          </option>
          {boards.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
              {b.privacy ? ` · ${b.privacy}` : ""}
              {b.pin_count != null ? ` · ${b.pin_count} pins` : ""}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="bo-button-secondary"
          data-testid="compose-pinterest-load-boards"
          onClick={() => onLoad({ refresh: false })}
          disabled={busy || !connection}
          style={{ whiteSpace: "nowrap" }}
        >
          {busy ? "Loading…" : (boards.length === 0 ? "Load boards" : "Reload")}
        </button>
        <button
          type="button"
          className="bo-button-secondary"
          data-testid="compose-pinterest-refresh-boards"
          onClick={() => onLoad({ refresh: true })}
          disabled={busy || !connection}
          title="Bypass the 1h cache and re-fetch from Pinterest."
          style={{ whiteSpace: "nowrap" }}
        >
          Refresh
        </button>
      </div>
      {cacheInfo && (
        <div
          data-testid="compose-pinterest-cache-info"
          style={{ fontSize: 11, color: "var(--bo-muted)" }}
        >
          {cacheInfo.cache_hit ? "From 1 h cache" : "Fresh from Pinterest"}
          {cacheInfo.sandbox ? " · Sandbox" : ""}
          {cacheInfo.cached_at
            ? ` · cached ${new Date(cacheInfo.cached_at).toLocaleTimeString()}`
            : ""}
        </div>
      )}
      {error && (
        <div
          data-testid="compose-pinterest-boards-error"
          style={{ fontSize: 12, color: "crimson" }}
        >
          {String(error)}
        </div>
      )}
      <div className="bo-field" style={{ margin: 0 }}>
        <label className="bo-label">
          Pin title ({title.length}/100)
        </label>
        <input
          className="bo-input"
          data-testid="compose-pinterest-title"
          value={title}
          onChange={(e) => onTitleChange(e.target.value.slice(0, 100))}
          maxLength={100}
          placeholder="Catchy title (shows under the Pin)"
        />
      </div>
      <div className="bo-field" style={{ margin: 0 }}>
        <label className="bo-label">
          Pin description ({description.length}/500)
        </label>
        <textarea
          className="bo-input"
          data-testid="compose-pinterest-description"
          rows={3}
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value.slice(0, 500))}
          maxLength={500}
          placeholder="What this Pin is about — Guardian-gated along with title."
        />
      </div>
    </div>
  );
}

